// https://atomiks.github.io/tippyjs/v6/all-props/
import tippy, { hideAll } from 'tippy.js';
import focusTrap from 'focus-trap';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import navigationSearch from './navigation.search';

navigationSearch();

window.tippyInstances = [];

// Init media query
const mql = window.matchMedia('(min-width: 1024px)');

// Modify/remove default dropdown styles
const stylesModifier = () => {
  let applyStyles = {};

  applyStyles = {
    name: 'applyStyles',
    enabled: false,
    phase: 'main',
  };

  return applyStyles;
};

// This is used for desktop and mobile
const initDropdown = ($dropdownItem, level = 2, wrapper = document) => {
  const $dropdownButton = $dropdownItem.querySelector('.o-navigation__link--dropdown');

  if (!$dropdownButton._tippy) { // eslint-disable-line
    const settings = { offset: level === 2 ? [0, -10] : 0 };
    let dropdownFocusTrap = null;

    tippy($dropdownButton, {
      content(reference) {
        const id = reference.getAttribute('aria-controls');
        const template = wrapper.querySelector(`#${id}`);

        return template.innerHTML;
      },
      onCreate(reference) {
        reference.popper.classList.add('o-navigation__dropdown');
        reference.popper.classList.add(`o-navigation__dropdown--level-${level}`);

        window.tippyInstances.push(reference);
        initMultilevelNavigation(reference.popper, level); // eslint-disable-line
      },
      onMount(reference) {
        $dropdownButton.setAttribute('aria-expanded', 'true');

        // FocusTrap Configuration
        dropdownFocusTrap = focusTrap(mql.matches ? $dropdownItem : reference.popper, {
          onDeactivate: () => reference.hide(),
          allowOutsideClick: () => true,
          clickOutsideDeactivates: false,
        });

        const $closeButton = reference.popper.querySelector('.o-navigation__back-button');

        $closeButton.addEventListener('click', () => {
          reference.hide();
        });

        $dropdownItem.classList.add('o-navigation__item--active');
        const hasCustomPlacement = reference.props.placement === reference.popperInstance.state.placement; // eslint-disable-line

        if (hasCustomPlacement && (mql.matches && level >= 3 || !mql.matches)) { // eslint-disable-line
          reference.setProps({
            popperOptions: {
              modifiers: [
                stylesModifier(),
              ],
            },
          });
        }
      },
      onShown(reference) {
        dropdownFocusTrap.activate();
        const $initialFocusEl = reference.popper.querySelector('[data-initialfocus]');
        $initialFocusEl.focus();
      },
      onHide() {
        $dropdownButton.setAttribute('aria-expanded', 'false');
        $dropdownItem.classList.remove('o-navigation__item--active');

        dropdownFocusTrap.deactivate();
      },
      hideOnClick: 'toggle',
      allowHTML: true,
      interactive: true,
      theme: 'light',
      animation: mql.matches ? 'shift-away-subtle' : 'shift-toward-extreme',
      trigger: mql.matches ? 'mouseenter click' : 'click', // mouseenter, click, focus
      arrow: false,
      interactiveBorder: 10,
      interactiveDebounce: 10,
      delay: [250, 300],
      duration: mql.matches ? 200 : 200,
      offset: settings.offset,
      placement: level >= 3 || !mql.matches ? 'right-start' : 'bottom-start', // right-start
      appendTo: mql.matches ? $dropdownItem : document.querySelector('.o-navigation__mobile .o-navigation--multilevel'), // $dropdownItem, $dropdownItem.parentNode,
      maxWidth: false,
    });
  }
};

const initMultilevelNavigation = (wrapper = document, level = 1) => {
  const dropdownItems = wrapper.querySelectorAll(`.o-navigation__list--level-${level} .o-navigation__item--dropdown`);

  if (dropdownItems.length > 0) {
    dropdownItems.forEach(($dropdownItem) => {
      initDropdown($dropdownItem, level + 1, wrapper);
    });
  }
};

/**
 * Mobile navigation controls
 */
const initMobileNavigationControls = () => {
  const $navigationControls = document.body.querySelectorAll('.o-navigation__control');

  $navigationControls.forEach(($control) => {
    let controlTargetFocusTrap = null;

    $control.addEventListener('click', (e) => {
      e.preventDefault();
      hideAll();
    });

    tippy($control, {
      content() {
        const controls = $control.getAttribute('aria-controls');
        const $target = document.querySelector(`#${controls}`);
        const content = $target ? $target.innerHTML : null;

        return content;
      },
      allowHTML: true,
      interactive: true,
      theme: 'light',
      animation: 'scale-subtle', // 'shift-away-subtle'
      trigger: 'click', // mouseenter, click, focus
      arrow: false,
      appendTo: document.querySelector('#navigation'),
      maxWidth: false,
      popperOptions: {
        modifiers: [
          stylesModifier(),
        ],
      },
      onShow() {
        hideAll();
        $navigationControls.forEach(($controlTemp) => {
          if ($control !== $controlTemp) {
            $controlTemp.setAttribute('tabindex', '-1');
          }
        });
      },
      onMount(reference) {
        const $overlayBackground = document.body.querySelector('.overlay-background');
        const matchesOnMount = mql.matches;

        reference.popper.classList.add('o-navigation__mobile');
        $overlayBackground.classList.add('overlay-background--active');

        window.addEventListener('resize', () => {
          if (matchesOnMount !== mql.matches) {
            reference.hide();
          }
        });

        controlTargetFocusTrap = focusTrap(reference.popper.parentNode, {
          clickOutsideDeactivates: true,
          allowOutsideClick: () => true,
          onActivate: () => {
            disableBodyScroll(document.body);
          },
          onDeactivate: () => {
            hideAll();
            enableBodyScroll(document.body);
            reference.hide();
          },
        });
        controlTargetFocusTrap.activate();
      },
      onShown() {
        initMultilevelNavigation();
      },
      onHide() {
        const $overlayBackground = document.body.querySelector('.overlay-background');

        controlTargetFocusTrap.deactivate();
        $overlayBackground.classList.remove('overlay-background--active');
        $navigationControls.forEach(($controlTemp) => {
          $controlTemp.removeAttribute('tabindex');
        });
        enableBodyScroll(document.body);
      },
    });
  });
};

initMobileNavigationControls();
initMultilevelNavigation();

window.addEventListener('resize', () => {
  // eslint-disable-next-line
  window.tippyInstances.forEach((tippy) => {
    tippy.destroy();
    window.tippyInstances = [];
  });

  initMultilevelNavigation();
});
